import { useMemo, type ReactNode } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export type RecurrenceUnit = "WEEK" | "MONTH" | "YEAR";

export interface RecurringScheduleValue {
  unit: RecurrenceUnit;
  intervalCount: number;
  /**
   * Browser-local datetime at hour resolution: `YYYY-MM-DDTHH:00`. Converted
   * to a UTC ISO instant via `toUtcHourlyIso` at submit time.
   */
  nextRunLocal: string;
  /** Edit-only: PAUSED toggle for an existing template. */
  status?: "ACTIVE" | "PAUSED";
}

export function defaultScheduleValue(): RecurringScheduleValue {
  // Default: weekly, interval 1, next run = next top-of-hour in viewer's TZ.
  const now = new Date();
  const next = new Date(now);
  next.setMinutes(0, 0, 0);
  next.setHours(next.getHours() + 1);
  return {
    unit: "WEEK",
    intervalCount: 1,
    nextRunLocal: toDatetimeLocalHourly(next),
  };
}

/** Convert a Date to the `YYYY-MM-DDTHH:00` form an `<input datetime-local>` accepts. */
export function toDatetimeLocalHourly(date: Date): string {
  const pad = (n: number): string => n.toString().padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:00`
  );
}

/**
 * Convert a `YYYY-MM-DDTHH:MM` browser-local string to a UTC ISO instant.
 *
 * The viewer's wall time is always interpreted in their browser's local
 * timezone — `new Date(YYYY-MM-DDTHH:00)` parses `datetime-local` strings as
 * local time per HTML/JS spec — and the resulting `.toISOString()` is the
 * UTC instant we persist. Users in different zones see the same persisted
 * UTC instant rendered in their own local time on the listing screen.
 */
export function toUtcHourlyIso(nextRunLocal: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(nextRunLocal)) return null;
  const date = new Date(nextRunLocal);
  if (Number.isNaN(date.getTime())) return null;
  // Floor to top-of-hour to match the schema's `HourlyAnchorSchema`.
  date.setMinutes(0, 0, 0);
  return date.toISOString();
}

interface Props {
  value: RecurringScheduleValue;
  onChange: (next: RecurringScheduleValue) => void;
  /** When true, exposes the ACTIVE/PAUSED toggle. Used by the edit form only. */
  showStatusToggle?: boolean;
  /** Renders the "next expense will be created at..." human preview. */
  showPreview?: boolean;
}

/**
 * Schedule-only editor used by the recurring-expense create/edit forms.
 *
 * Restrictions enforced inline:
 * - intervalCount: 1..52 integer
 * - Date+hour selector floors to the top of the hour
 * - Unit cannot encode sub-weekly cadence (the type only admits WEEK/MONTH/YEAR)
 */
export function RecurringScheduleFields({
  value,
  onChange,
  showStatusToggle,
  showPreview = true,
}: Props): ReactNode {
  const intervalText = value.intervalCount.toString();

  const utcIso = useMemo(
    () => toUtcHourlyIso(value.nextRunLocal),
    [value.nextRunLocal],
  );

  const previewText = useMemo(() => {
    if (!utcIso) return "Pick a date and time.";
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(utcIso));
  }, [utcIso]);

  return (
    <section className="space-y-4 rounded-md bg-surface-container-lowest p-5">
      <h2 className="text-headline-md">Schedule</h2>

      <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
        <div className="space-y-2">
          <Label htmlFor="recurring-interval">Every</Label>
          <Input
            id="recurring-interval"
            type="number"
            min="1"
            max="52"
            step="1"
            inputMode="numeric"
            value={intervalText}
            onChange={(e) =>
              onChange({
                ...value,
                intervalCount: clampInterval(e.target.value),
              })
            }
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="recurring-unit">Unit</Label>
          <select
            id="recurring-unit"
            value={value.unit}
            onChange={(e) =>
              onChange({ ...value, unit: e.target.value as RecurrenceUnit })
            }
            className="flex h-11 w-full rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <option value="WEEK">week(s)</option>
            <option value="MONTH">month(s)</option>
            <option value="YEAR">year(s)</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="recurring-next">Starting</Label>
        <Input
          id="recurring-next"
          type="datetime-local"
          step="3600"
          value={value.nextRunLocal}
          onChange={(e) =>
            onChange({
              ...value,
              nextRunLocal: floorToHour(e.target.value),
            })
          }
          required
        />
      </div>

      {showStatusToggle ? (
        <label className="flex items-center gap-2 text-body-md">
          <input
            type="checkbox"
            checked={value.status === "PAUSED"}
            onChange={(e) =>
              onChange({
                ...value,
                status: e.target.checked ? "PAUSED" : "ACTIVE",
              })
            }
            className="size-4 accent-primary"
          />
          Paused (no expenses will be created until resumed)
        </label>
      ) : null}

      {showPreview ? (
        <p className="text-label-sm text-on-surface-variant">
          Next expense will be created on {previewText}.
        </p>
      ) : null}
    </section>
  );
}

function clampInterval(raw: string): number {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(52, Math.max(1, n));
}

/** Drop the minutes part — `<input datetime-local>` lets users type `:30`. */
function floorToHour(raw: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return raw;
  return `${raw.slice(0, 13)}:00`;
}
